import { useState, useEffect } from 'react';
import { Star, Plus, Trash2, MessageSquare, Calendar, User, Edit2 } from 'lucide-react';

const StepReviews = ({ formData, handleInputChange }) => {
  const [reviews, setReviews] = useState(formData.reviews || []);

  // Sync reviews when formData changes (for editing existing trips)
  useEffect(() => {
    if (formData.reviews) {
      setReviews(formData.reviews);
    }
  }, [formData.reviews]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingReviewId, setEditingReviewId] = useState(null);
  const [newReview, setNewReview] = useState({
    reviewerName: '',
    reviewerLocation: '',
    title: '',
    date: '',
    content: '',
    ratings: {
      valueForMoney: 5,
      food: 5,
      qualityOfActivity: 5,
      accommodationFacilities: 5,
      location: 5,
      overallImpression: 5,
    },
    hostResponse: '',
    hostResponseDate: '',
  });

  const ratingCategories = [
    { key: 'valueForMoney', label: 'Value for money' },
    { key: 'food', label: 'Food' },
    { key: 'qualityOfActivity', label: 'Quality of activity' },
    { key: 'accommodationFacilities', label: 'Accommodation & facilities' },
    { key: 'location', label: 'Location' },
    { key: 'overallImpression', label: 'Overall impression' },
  ];

  const handleAddReview = () => {
    if (!newReview.reviewerName || !newReview.title || !newReview.content || !newReview.date) {
      alert('Please fill in all required fields');
      return;
    }

    const review = {
      ...newReview,
      id: Date.now().toString(),
    };

    const updated = [...reviews, review];
    setReviews(updated);
    handleInputChange('reviews', updated);
    
    // Reset form
    setNewReview({
      reviewerName: '',
      reviewerLocation: '',
      title: '',
      date: '',
      content: '',
      ratings: {
        valueForMoney: 5,
        food: 5,
        qualityOfActivity: 5,
        accommodationFacilities: 5,
        location: 5,
        overallImpression: 5,
      },
      hostResponse: '',
      hostResponseDate: '',
    });
    setShowAddForm(false);
  };

  const handleRemoveReview = (reviewId) => {
    const updated = reviews.filter(r => r.id !== reviewId);
    setReviews(updated);
    handleInputChange('reviews', updated);
  };

  const handleEditReview = (reviewId) => {
    const review = reviews.find(r => r.id === reviewId);
    if (review) {
      setEditingReviewId(reviewId);
      setNewReview({
        reviewerName: review.reviewerName || '',
        reviewerLocation: review.reviewerLocation || '',
        title: review.title || '',
        date: review.date || '',
        content: review.content || '',
        ratings: review.ratings || {
          valueForMoney: 5,
          food: 5,
          qualityOfActivity: 5,
          accommodationFacilities: 5,
          location: 5,
          overallImpression: 5,
        },
        hostResponse: review.hostResponse || '',
        hostResponseDate: review.hostResponseDate || '',
      });
      setShowAddForm(false);
    }
  };

  const handleUpdateReview = () => {
    if (!newReview.reviewerName || !newReview.title || !newReview.content || !newReview.date) {
      alert('Please fill in all required fields');
      return;
    }

    const updated = reviews.map(r => 
      r.id === editingReviewId 
        ? { ...r, ...newReview }
        : r
    );
    setReviews(updated);
    handleInputChange('reviews', updated);
    
    // Reset form
    setNewReview({
      reviewerName: '',
      reviewerLocation: '',
      title: '',
      date: '',
      content: '',
      ratings: {
        valueForMoney: 5,
        food: 5,
        qualityOfActivity: 5,
        accommodationFacilities: 5,
        location: 5,
        overallImpression: 5,
      },
      hostResponse: '',
      hostResponseDate: '',
    });
    setEditingReviewId(null);
    setShowAddForm(false);
  };

  const handleCancelEdit = () => {
    setEditingReviewId(null);
    setNewReview({
      reviewerName: '',
      reviewerLocation: '',
      title: '',
      date: '',
      content: '',
      ratings: {
        valueForMoney: 5,
        food: 5,
        qualityOfActivity: 5,
        accommodationFacilities: 5,
        location: 5,
        overallImpression: 5,
      },
      hostResponse: '',
      hostResponseDate: '',
    });
    setShowAddForm(false);
  };

  const handleRatingChange = (category, value) => {
    setNewReview(prev => ({
      ...prev,
      ratings: {
        ...prev.ratings,
        [category]: parseInt(value),
      },
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="space-y-6">
      {/* Temporary Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> This feature allows you to import reviews from other platforms temporarily. 
          In the future, reviews will be added directly by clients through the Spirara platform.
        </p>
      </div>

      {/* Existing Reviews */}
      {reviews.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-800">Imported Reviews</h3>
          {reviews.map((review) => (
            <div key={review.id} className="bg-white border border-gray-200 rounded-lg p-6 relative">
              <div className="absolute top-4 right-4 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleEditReview(review.id)}
                  className="text-emerald-600 hover:text-emerald-700 transition-colors"
                  title="Edit review"
                >
                  <Edit2 className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={() => handleRemoveReview(review.id)}
                  className="text-red-500 hover:text-red-700 transition-colors"
                  title="Delete review"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>

              {/* Reviewer Info */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-gray-500" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Review by {review.reviewerName}</p>
                    {review.reviewerLocation && (
                      <p className="text-sm text-gray-500">from {review.reviewerLocation}</p>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">{formatDate(review.date)}</p>
                </div>
              </div>

              {/* Review Title */}
              <h4 className="text-lg font-semibold text-gray-900 mb-3">{review.title}</h4>

              {/* Review Content */}
              <div className="mb-4">
                <p className="text-gray-700 leading-relaxed">{review.content}</p>
              </div>

              {/* Star Ratings */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                {ratingCategories.map((category) => (
                  <div key={category.key} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">{category.label}:</span>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`w-4 h-4 ${
                            star <= (review.ratings?.[category.key] || 5)
                              ? 'fill-yellow-400 text-yellow-400'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Host Response */}
              {review.hostResponse && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Organizer response:</p>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-700 mb-2">{review.hostResponse}</p>
                    {review.hostResponseDate && (
                      <p className="text-xs text-gray-500">{formatDate(review.hostResponseDate)}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Review Form */}
      {(showAddForm || editingReviewId) ? (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            {editingReviewId ? 'Edit Review' : 'Add New Review'}
          </h3>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reviewer Name *
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  value={newReview.reviewerName}
                  onChange={(e) => setNewReview({ ...newReview, reviewerName: e.target.value })}
                  placeholder="e.g., Marvin B."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reviewer Location
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  value={newReview.reviewerLocation}
                  onChange={(e) => setNewReview({ ...newReview, reviewerLocation: e.target.value })}
                  placeholder="e.g., Germany"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Review Title *
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                value={newReview.title}
                onChange={(e) => setNewReview({ ...newReview, title: e.target.value })}
                placeholder="e.g., Do it. Wonderful once in a Lifestyle experience."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Review Date *
              </label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                value={newReview.date}
                onChange={(e) => setNewReview({ ...newReview, date: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Review Content *
              </label>
              <textarea
                rows="6"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                value={newReview.content}
                onChange={(e) => setNewReview({ ...newReview, content: e.target.value })}
                placeholder="Enter the review content..."
              />
              <p className="mt-1 text-xs text-gray-500">
                {newReview.content.length.toLocaleString()} characters
              </p>
            </div>

            {/* Star Ratings */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Ratings (1-5 stars) *
              </label>
              <div className="grid grid-cols-2 gap-4">
                {ratingCategories.map((category) => (
                  <div key={category.key}>
                    <label className="block text-xs text-gray-600 mb-1">{category.label}</label>
                    <div className="flex items-center gap-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => handleRatingChange(category.key, star)}
                          className="focus:outline-none"
                        >
                          <Star
                            className={`w-5 h-5 ${
                              star <= (newReview.ratings[category.key] || 5)
                                ? 'fill-yellow-400 text-yellow-400'
                                : 'text-gray-300'
                            }`}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Host Response */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Host Response (Optional)
              </label>
              <textarea
                rows="4"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                value={newReview.hostResponse}
                onChange={(e) => setNewReview({ ...newReview, hostResponse: e.target.value })}
                placeholder="Enter your response to this review..."
              />
              <p className="mt-1 text-xs text-gray-500">
                {newReview.hostResponse.length.toLocaleString()} characters
              </p>
            </div>

            {newReview.hostResponse && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Response Date
                </label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  value={newReview.hostResponseDate}
                  onChange={(e) => setNewReview({ ...newReview, hostResponseDate: e.target.value })}
                />
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={editingReviewId ? handleUpdateReview : handleAddReview}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
              >
                {editingReviewId ? 'Update Review' : 'Add Review'}
              </button>
              <button
                type="button"
                onClick={handleCancelEdit}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowAddForm(true)}
          className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition-colors font-medium"
        >
          <Plus className="w-5 h-5" /> Add Review
        </button>
      )}
    </div>
  );
};

export default StepReviews;

